import React, { useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../../services/adminService';
import { Plus, Pencil, Trash2, Eye, EyeOff, X, Shield } from 'lucide-react';

const emptyForm = {
  name: '',
  slug: '',
  short_description: '',
  description: '',
  price: '',
  billing_type: 'recurring',
  billing_interval_months: '12',
  duration_months: '12',
  benefitsText: '',
  is_popular: false,
  is_active: true,
  position: '0',
  card_delivery_included: true,
};

const MembershipPlansAdmin = () => {
  const queryClient = useQueryClient();
  const formRef = useRef(null);
  const [formData, setFormData] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);

  const { data: items = [], isLoading } = useQuery({
    queryKey: ['membershipPlans'],
    queryFn: async () => {
      const { data, error } = await supabase.from('membership_plans').select('*').order('position', { ascending: false });
      if (error) throw error;
      return data || [];
    },
  });

  const saveMutation = useMutation({
    mutationFn: async ({ id, payload }) => {
      if (id) {
        const { error } = await supabase.from('membership_plans').update(payload).eq('id', id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from('membership_plans').insert([payload]);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['membershipPlans']);
      setFormData(emptyForm);
      setEditingId(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id) => {
      const { error } = await supabase.from('membership_plans').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries(['membershipPlans']),
  });

  const buildPayload = () => {
    const benefits = formData.benefitsText
      .split('\n')
      .map((s) => s.trim())
      .filter(Boolean);
    return {
      name: formData.name.trim(),
      slug: formData.slug.trim() || formData.name.trim().toLowerCase().replace(/\s+/g, '-'),
      short_description: formData.short_description.trim() || null,
      description: formData.description.trim() || null,
      price: Number(formData.price) || 0,
      billing_type: formData.billing_type,
      billing_interval_months: Number(formData.billing_interval_months) || 12,
      duration_months: Number(formData.duration_months) || 12,
      benefits,
      is_popular: formData.is_popular,
      is_active: formData.is_active,
      position: Number(formData.position) || 0,
      card_delivery_included: formData.card_delivery_included,
      updated_at: new Date().toISOString(),
    };
  };

  const handleSave = () => {
    if (!formData.name.trim() || !formData.price) {
      alert('Name and price are required');
      return;
    }
    saveMutation.mutate({ id: editingId, payload: buildPayload() });
  };

  const handleEdit = (item) => {
    setEditingId(item.id);
    setFormData({
      name: item.name || '',
      slug: item.slug || '',
      short_description: item.short_description || '',
      description: item.description || '',
      price: String(item.price ?? ''),
      billing_type: item.billing_type || 'recurring',
      billing_interval_months: String(item.billing_interval_months ?? 12),
      duration_months: String(item.duration_months ?? 12),
      benefitsText: (item.benefits || []).join('\n'),
      is_popular: !!item.is_popular,
      is_active: item.is_active !== false,
      position: String(item.position ?? 0),
      card_delivery_included: item.card_delivery_included !== false,
    });
    formRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const billingLabel = (item) => {
    if (item.billing_type === 'one_time') return `${item.duration_months} mo · one-time`;
    return `Every ${item.billing_interval_months} mo · auto-renew`;
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">RSA Membership Plans</h1>
        <p className="text-gray-600 mt-1">
          Manage subscription plans shown on customer app. Recurring plans auto-renew after billing interval.
        </p>
      </div>

      <div ref={formRef} className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            {editingId ? <Pencil size={20} /> : <Plus size={20} />}
            {editingId ? 'Edit Plan' : 'Add Plan'}
          </h2>
          {editingId && (
            <button onClick={() => { setEditingId(null); setFormData(emptyForm); }} className="text-sm text-gray-600 flex items-center gap-1">
              <X size={16} /> Cancel
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <input className="input border rounded-lg px-3 py-2" placeholder="Plan name *" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} />
          <input className="input border rounded-lg px-3 py-2" placeholder="Slug (unique)" value={formData.slug} onChange={(e) => setFormData({ ...formData, slug: e.target.value })} />
          <input className="input border rounded-lg px-3 py-2" placeholder="Short tagline" value={formData.short_description} onChange={(e) => setFormData({ ...formData, short_description: e.target.value })} />
          <input className="input border rounded-lg px-3 py-2" type="number" placeholder="Price (INR) *" value={formData.price} onChange={(e) => setFormData({ ...formData, price: e.target.value })} />
          <select className="border rounded-lg px-3 py-2" value={formData.billing_type} onChange={(e) => setFormData({ ...formData, billing_type: e.target.value })}>
            <option value="recurring">Recurring (auto-renew)</option>
            <option value="one_time">One-time payment</option>
          </select>
          <input className="input border rounded-lg px-3 py-2" type="number" placeholder="Billing every X months" value={formData.billing_interval_months} onChange={(e) => setFormData({ ...formData, billing_interval_months: e.target.value })} />
          <input className="input border rounded-lg px-3 py-2" type="number" placeholder="Total validity (months)" value={formData.duration_months} onChange={(e) => setFormData({ ...formData, duration_months: e.target.value })} />
          <input className="input border rounded-lg px-3 py-2" type="number" placeholder="Display priority" value={formData.position} onChange={(e) => setFormData({ ...formData, position: e.target.value })} />
          <textarea className="border rounded-lg px-3 py-2 md:col-span-2" rows={2} placeholder="Description" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} />
          <textarea className="border rounded-lg px-3 py-2 md:col-span-2" rows={4} placeholder="Benefits (one per line)" value={formData.benefitsText} onChange={(e) => setFormData({ ...formData, benefitsText: e.target.value })} />
        </div>

        <div className="flex flex-wrap gap-4 text-sm">
          <label className="flex items-center gap-2"><input type="checkbox" checked={formData.is_popular} onChange={(e) => setFormData({ ...formData, is_popular: e.target.checked })} /> Most popular</label>
          <label className="flex items-center gap-2"><input type="checkbox" checked={formData.is_active} onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })} /> Active on app</label>
          <label className="flex items-center gap-2"><input type="checkbox" checked={formData.card_delivery_included} onChange={(e) => setFormData({ ...formData, card_delivery_included: e.target.checked })} /> RSA card delivery</label>
        </div>

        <button onClick={handleSave} disabled={saveMutation.isPending} className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600">
          {editingId ? 'Save Changes' : 'Publish Plan'}
        </button>
      </div>

      <div className="bg-white rounded-xl border shadow-sm divide-y">
        <div className="px-6 py-4 font-semibold">Published Plans</div>
        {isLoading ? <div className="p-8 text-center text-gray-500">Loading…</div> : items.length === 0 ? (
          <div className="p-8 text-center text-gray-500">No plans. Run supabase/create-membership-plans.sql or add above.</div>
        ) : items.map((item) => (
          <div key={item.id} className="p-4 flex flex-col md:flex-row md:items-center gap-4 justify-between">
            <div>
              <div className="flex items-center gap-2">
                <Shield size={18} className="text-primary-600" />
                <span className="font-bold">{item.name}</span>
                {item.is_popular && <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded">Popular</span>}
              </div>
              <p className="text-sm text-gray-600 mt-1">₹{item.price} · {billingLabel(item)}</p>
              <p className="text-xs text-gray-400">{item.is_active ? 'Visible on app' : 'Hidden'}</p>
            </div>
            <div className="flex gap-2">
              <button onClick={() => handleEdit(item)} className="px-3 py-1.5 bg-blue-500 text-white rounded-lg text-sm flex items-center gap-1"><Pencil size={14} /> Edit</button>
              <button onClick={() => saveMutation.mutate({ id: item.id, payload: { is_active: !item.is_active, updated_at: new Date().toISOString() } })} className={`px-3 py-1.5 rounded-lg text-sm text-white ${item.is_active ? 'bg-amber-500' : 'bg-green-600'}`}>
                {item.is_active ? <><EyeOff size={14} className="inline" /> Hide</> : <><Eye size={14} className="inline" /> Show</>}
              </button>
              <button onClick={() => window.confirm('Delete plan?') && deleteMutation.mutate(item.id)} className="px-3 py-1.5 bg-red-500 text-white rounded-lg text-sm flex items-center gap-1"><Trash2 size={14} /> Delete</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default MembershipPlansAdmin;
